import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { PageHeader, REMOTE_PERMISSIONS, useContainerQuery } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useIsInstitutionAdmin } from '@backrs_ui/grow-ui/src/hooks/useIsInstitutionAdmin';
import { usePageTabs } from '@backrs_ui/grow-ui/src/hooks/usePageTabs';
import { usePlatformMode } from '@backrs_ui/grow-ui/src/hooks/usePlatformMode';
import {
  ALL_FILTER_VALUE,
  NO_DATA_FILTER_VALUE,
  useGetCoachingAnalyticsAyotQuery,
  useGetCoachingAnalyticsBlueprintQuery,
  useGetCoachingAnalyticsCoachingQuery,
  useGetCoachingAnalyticsEngagementQuery,
  useGetCoachingAnalyticsFilterOptionsQuery,
  useGetCoachingAnalyticsLifeReadinessQuery,
  useGetCoachingAnalyticsOverviewQuery,
  useGetCoachingAnalyticsToolUsageQuery,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  PLATFORM_OPERATOR_CONTEXT,
  selectStoredOperatorInstitutionId,
} from '@backrs_ui/grow-ui/src/redux/operatorContext';

import { Box, type SxProps, type Theme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { ANALYTICS_SECTION_ID } from './analytics.anchors';
import { programLabel } from './analytics.labels';
import {
  ANALYTICS_TAB_LABEL,
  ANALYTICS_TABS,
  type AnalyticsTab,
  analyticsTabFromSlug,
  analyticsTabPath,
} from './analytics.tabs';
import {
  AnalyticsFilterBar,
  type AnalyticsFilterValues,
  type AnalyticsMultiFilterKey,
  DEFAULT_FILTERS,
  type FilterOption,
} from './AnalyticsFilterBar';
import { AnalyticsFilterProvider } from './AnalyticsFilterContext';
import { SectionState } from './AnalyticsSectionState';
import { AnalyticsTabLink } from './AnalyticsTabLink';
import { BlueprintAnalyticsSection } from './BlueprintCards';
import { useAgentContext } from './CoachingAnalyticsPageContainer.agentContext';
import { CoachingSection } from './CoachingCards';
import { EngagementSection } from './EngagementCards';
import { LifeReadinessSection } from './LifeReadinessCards';
import { OnTrackSection } from './OnTrackCards';
import { AnalyticsOverviewGrid, MyCoachingSection } from './OverviewCards';
import { ToolUsageCard } from './ToolUsageCards';

const { coachingAnalytics } = appStrings.modules;
const { filters: filterStrings, successSystem } = coachingAnalytics;

/** The way in: choosing it changes the context rather than the narrowing. */
const PLATFORM_ENTRY_OPTION: FilterOption = {
  value: PLATFORM_OPERATOR_CONTEXT,
  label: filterStrings.platformOption,
};

// The plain Student role is every student's default, so it is noise as a
// program filter.
const DEFAULT_PROGRAM = 'student';

export const CoachingAnalyticsPageContainer = () => {
  const { tab: tabSlug } = useParams<{ tab: string }>();
  const tab = analyticsTabFromSlug(tabSlug);
  // Measures the column, not the window - the resizable chat panel takes width out of the page.
  // Below 650 the filter fields need a third row.
  const [containerRef, isCompact] = useContainerQuery(650);
  const [filters, setFilters] = useState<AnalyticsFilterValues>(DEFAULT_FILTERS);

  const operatorInstitutionId = useAppSelector(selectStoredOperatorInstitutionId);
  const [narrowedUnder, setNarrowedUnder] = useState(operatorInstitutionId);

  // Every dimension here names something belonging to the operator root it was
  // chosen under, so none of them survives a change of one. Reset while
  // rendering rather than from an effect: the panels below read `filters` in
  // this same pass, and an effect would let the active panels refetch under the
  // old root's narrowing first, which the api refuses.
  if (narrowedUnder !== operatorInstitutionId) {
    setNarrowedUnder(operatorInstitutionId);
    setFilters(DEFAULT_FILTERS);
  }

  const isInstitutionAdmin = useIsInstitutionAdmin();
  const pageTabs = useMemo(
    () =>
      ANALYTICS_TABS.map((option) => ({
        id: option,
        label: ANALYTICS_TAB_LABEL[option],
        path: analyticsTabPath(APP_ROUTE.COACHING_ANALYTICS, option),
      })),
    [],
  );

  usePageTabs(pageTabs);
  const shows = (panel: AnalyticsTab) => tab === 'overview' || tab === panel;
  // The Coaching tab is two different pages: the admin's coach roster, and a
  // coach's own students and activity, which come from the overview response.
  const showsCoachRoster = isInstitutionAdmin && shows('coaching');
  const showsMyCoaching = !isInstitutionAdmin && shows('coaching');
  // The read is only ever asked for by a caller who could act on its answer, so an admin
  // without the grant issues no request this page did not issue before.
  const canAccessOperatorFleet = useHasPermission(REMOTE_PERMISSIONS.ACCESS_OPERATOR_FLEET);
  const platform = usePlatformMode({ enabled: isInstitutionAdmin && canAccessOperatorFleet });

  const handleProgramChange = (value: string) => {
    setFilters((prev) => ({ ...prev, programs: value }));
  };

  const handleMultiFilterChange = (key: AnalyticsMultiFilterKey, values: string[]) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
  };

  // The context change resets the narrowings, which is what returns the select to All.
  const handleInstitutionAction = (value: string) => {
    if (value === PLATFORM_OPERATOR_CONTEXT) platform.enter();
  };

  // Options are re-fetched on every filter change, so each list stays narrowed
  // to what the current selection allows.
  const analyticsQuery = { filter: filters };
  const { data: options, isError: isOptionsError } =
    useGetCoachingAnalyticsFilterOptionsQuery(analyticsQuery);
  // A selected coach drops out of the options once other filters leave them no
  // students; the name last offered keeps them listed under it.
  const [coachNames, setCoachNames] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    const offered = options?.coaches ?? [];

    if (!offered.length) return;

    setCoachNames((seen) => {
      const names = new Map(seen);

      offered.forEach((coach) => names.set(coach.value, coach.name));

      return names;
    });
  }, [options]);

  const queryFor = (panel: AnalyticsTab) => (shows(panel) ? analyticsQuery : skipToken);
  const overviewQuery = useGetCoachingAnalyticsOverviewQuery(
    shows('overview') || showsMyCoaching ? analyticsQuery : skipToken,
  );
  const blueprintQuery = useGetCoachingAnalyticsBlueprintQuery(queryFor('blueprint'));
  const ayotQuery = useGetCoachingAnalyticsAyotQuery(queryFor('onTrack'));
  const lifeReadinessQuery = useGetCoachingAnalyticsLifeReadinessQuery(queryFor('lifeReadiness'));
  const engagementQuery = useGetCoachingAnalyticsEngagementQuery(queryFor('engagement'));
  const coachingQuery = useGetCoachingAnalyticsCoachingQuery(
    showsCoachRoster ? analyticsQuery : skipToken,
  );
  const toolUsageQuery = useGetCoachingAnalyticsToolUsageQuery(queryFor('engagement'));

  const institutionOptions = isInstitutionAdmin
    ? (options?.institutions ?? []).map((institution) => ({
        value: String(institution.id),
        label: institution.name,
      }))
    : undefined;

  const programOptions = (options?.programs ?? [])
    .filter((program) => program.value !== DEFAULT_PROGRAM)
    .map((program) => ({
      value: program.value,
      label: programLabel(program.value),
      count: program.count,
    }));

  const gradeOptions = (options?.grades ?? []).map((grade) => ({
    value: grade.value,
    label:
      grade.value === NO_DATA_FILTER_VALUE ? filterStrings.noGrade : (grade.name ?? grade.value),
    count: grade.count,
  }));

  const offeredCoaches = options?.coaches ?? [];
  const coachOptions = isInstitutionAdmin
    ? [
        ...offeredCoaches.map((coach) => ({
          value: coach.value,
          label: coach.name,
          count: coach.count,
        })),
        ...filters.coaches
          .filter((value) => !offeredCoaches.some((coach) => coach.value === value))
          .map((value) => ({ value, label: coachNames.get(value) ?? value, count: 0 })),
      ]
    : undefined;

  const identityOptions = (
    rows: readonly Readonly<{ value: string; name: string | null; count: number }>[],
    noneLabel: string,
  ) =>
    rows.map((row) => ({
      value: row.value,
      label: row.name ?? noneLabel,
      count: row.count,
    }));
  const genderOptions = identityOptions(options?.genders ?? [], filterStrings.noGender);
  const ethnicityOptions = identityOptions(options?.ethnicities ?? [], filterStrings.noEthnicity);
  const enrollmentOptions = identityOptions(
    options?.enrollment_types ?? [],
    filterStrings.noEnrollmentType,
  );
  const cohortOptions = identityOptions(options?.cohorts ?? [], filterStrings.noCohort);

  const { hash } = useLocation();
  // Sections render as their queries resolve, so scrolling to a hash anchor is
  // only reliable once everything above it has laid out.
  const sectionsReady = [
    overviewQuery,
    blueprintQuery,
    ayotQuery,
    lifeReadinessQuery,
    engagementQuery,
    coachingQuery,
    toolUsageQuery,
  ].every((query) => !query.isLoading);

  // Changing a filter re-runs every query, so sectionsReady flips again; without this the
  // page would scroll back to the anchor on each change for as long as the hash is in the URL.
  const scrolledHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hash || !sectionsReady || scrolledHashRef.current === hash) return;

    scrolledHashRef.current = hash;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, sectionsReady]);

  useAgentContext(tab);

  if (tab == null) {
    return <Navigate to={APP_ROUTE.COACHING_ANALYTICS} replace />;
  }

  const isOverview = tab === 'overview';
  const sectionAction = (target: AnalyticsTab) =>
    isOverview ? <AnalyticsTabLink key={`${target}-see-more`} tab={target} /> : undefined;

  const summarize = (label: string, selected: readonly string[], all: readonly FilterOption[]) =>
    selected.length === 0
      ? null
      : `${label}: ${selected
          .map((value) => all.find((option) => option.value === value)?.label ?? value)
          .join(', ')}`;
  const filterContext = {
    filter: filters,
    summary: [
      institutionOptions &&
        summarize(filterStrings.institution, filters.institution, institutionOptions),
      filters.programs !== ALL_FILTER_VALUE &&
        `${filterStrings.programs}: ${programLabel(filters.programs)}`,
      summarize(filterStrings.grade, filters.grade, gradeOptions),
      summarize(filterStrings.enrollment, filters.enrollment, enrollmentOptions),
      coachOptions && summarize(filterStrings.coaches, filters.coaches, coachOptions),
      summarize(filterStrings.cohorts, filters.cohorts, cohortOptions),
      summarize(filterStrings.gender, filters.gender, genderOptions),
      summarize(filterStrings.ethnicity, filters.ethnicity, ethnicityOptions),
    ].filter((part): part is string => Boolean(part)),
  };

  return (
    <AnalyticsFilterProvider value={filterContext}>
      <Box sx={pageSx} ref={containerRef}>
        <AnalyticsFilterBar
          isCompact={isCompact}
          isOptionsUnavailable={isOptionsError}
          filters={filters}
          onProgramChange={handleProgramChange}
          onMultiFilterChange={handleMultiFilterChange}
          onClear={() => setFilters(DEFAULT_FILTERS)}
          institutionOptions={institutionOptions}
          institutionActions={platform.available && !platform.active ? [PLATFORM_ENTRY_OPTION] : []}
          onInstitutionAction={handleInstitutionAction}
          institutionAllLabel={platform.active ? filterStrings.allInstitutions : undefined}
          programOptions={programOptions}
          gradeOptions={gradeOptions}
          enrollmentOptions={enrollmentOptions}
          coachOptions={coachOptions}
          genderOptions={genderOptions}
          ethnicityOptions={ethnicityOptions}
          cohortOptions={cohortOptions}
        />
        {isOverview && (
          <Box id={ANALYTICS_SECTION_ID.overview} sx={anchorSx}>
            <SectionState query={overviewQuery}>
              {(overview) => <AnalyticsOverviewGrid overview={overview} />}
            </SectionState>
          </Box>
        )}
        {isOverview && (
          <PageHeader
            title={successSystem.title}
            subtitle={successSystem.subtitle}
            maxWidth={null}
            rightComponents={[
              <LubavButton
                key="discuss-lubav"
                id="coaching-analytics-discuss-lubav"
                label={successSystem.discussWithLubav}
                promptButtonId="coaching_analytics_success_system"
              />,
            ]}
          />
        )}
        {shows('blueprint') && (
          <SectionState query={blueprintQuery}>
            {(blueprint) => (
              <BlueprintAnalyticsSection
                data={blueprint}
                action={sectionAction('blueprint')}
                detail={!isOverview}
              />
            )}
          </SectionState>
        )}
        {shows('onTrack') && (
          <SectionState query={ayotQuery}>
            {(ayot) => (
              <OnTrackSection data={ayot} action={sectionAction('onTrack')} detail={!isOverview} />
            )}
          </SectionState>
        )}
        {shows('lifeReadiness') && (
          <SectionState query={lifeReadinessQuery}>
            {(lifeReadiness) => (
              <LifeReadinessSection
                data={lifeReadiness}
                action={sectionAction('lifeReadiness')}
                detail={!isOverview}
              />
            )}
          </SectionState>
        )}
        {shows('engagement') && (
          <Box id={ANALYTICS_SECTION_ID.engagement} sx={anchorSx}>
            <SectionState query={engagementQuery}>
              {(engagement) => (
                <EngagementSection
                  data={engagement}
                  action={sectionAction('engagement')}
                  detail={!isOverview}
                  toolUsage={
                    <SectionState query={toolUsageQuery}>
                      {(toolUsage) => <ToolUsageCard data={toolUsage} />}
                    </SectionState>
                  }
                />
              )}
            </SectionState>
          </Box>
        )}
        {showsCoachRoster && (
          <Box id={ANALYTICS_SECTION_ID.coaching} sx={anchorSx}>
            <SectionState query={coachingQuery}>
              {(coaching) => (
                <CoachingSection
                  data={coaching}
                  action={sectionAction('coaching')}
                  detail={!isOverview}
                />
              )}
            </SectionState>
          </Box>
        )}
        {showsMyCoaching && (
          <Box id={ANALYTICS_SECTION_ID.coaching} sx={anchorSx}>
            <SectionState query={overviewQuery}>
              {(overview) => (
                <MyCoachingSection
                  overview={overview}
                  action={sectionAction('coaching')}
                  detail={!isOverview}
                />
              )}
            </SectionState>
          </Box>
        )}
      </Box>
    </AnalyticsFilterProvider>
  );
};

const anchorSx: SxProps<Theme> = ({ backrs }) => ({
  scrollMarginTop: backrs.spacing['spacing-2xl'],
});

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  // shell content wrapper already adds spacing-2xl (20px) on top, so 4px here yields 24px
  paddingTop: backrs.spacing['spacing-xs'],
  paddingBottom: backrs.spacing['spacing-3xl'],
  width: '100%',
});
